import { css, styled } from '../../../../../../core/theme/styled';
import { InputBaseStyles, StyledIconContainer, StyledLabel } from './InputField.styles';

type StyledInputProps = {
  alignIcon?: boolean; // Align the icon with the input when a floating label exist and not between them.
  floatingLabel?: boolean; // needed for InputBaseStyles
  error?: boolean; // needed for InputBaseStyles
};

// styled-components has a bug that filters out the enterKeyHint prop.
// https://github.com/styled-components/styled-components/issues/3417
export const InputBase = styled.input
  .attrs((props) => {
    // For the floating label a placeholder is mandatory, to make usage of the :placeholder-shown state.
    // Safari does not interpret the placeholder with an empty value, that's why we need at least a space.
    return { placeholder: props.placeholder || ' ' };
  })
  .withConfig({
    shouldForwardProp: (prop, defaultValidatorFn) =>
      ['enterKeyHint'].includes(prop) || defaultValidatorFn(prop),
  })<StyledInputProps>`
  ${InputBaseStyles}

  &::placeholder {
    color: ${(props) => props.theme.configuration.secondaryTextColor};
  }

  &::-ms-clear {
    display: none;
  }

  &::-webkit-credentials-auto-fill-button {
    visibility: hidden;
  }
`;

export const StyledTextField = styled(InputBase)`
  ${({ floatingLabel }) =>
    floatingLabel &&
    css`
      &::placeholder {
        opacity: 0;
      }

      &:focus &::placeholder {
        opacity: 1;
      }
    `};

  /**
  * When the element is focused, remove the label transform.
  * Also, do this when the placeholder is _not_ shown, i.e. when
  * there's something in the input at all.
  */
  &:not(:placeholder-shown),
  &:focus {
    & + ${StyledLabel} {
      transform: scale(0.85)
        translate(${(props) => props.theme.sizes.normedium}, ${(props) => props.theme.sizes.xsmall});
    }
    ${({ alignIcon }) =>
      alignIcon &&
      css`
        & ~ ${StyledIconContainer} {
          top: 0.6rem;
        }
      `};
  }
`;

export const StyledTextFieldWrapper = styled.div`
  /* StyledIconContainer is positioned absolutely in relation to this wrapper */
  position: relative;

  /* when an icon is at the beginning, we add a padding-left to the input */
  ${StyledIconContainer}:first-child {
    right: auto;
    left: ${(props) => props.theme.sizes.normal};

    & ~ ${StyledLabel} {
      left: 1.825rem;
    }
    & + ${StyledTextField} {
      padding-left: 3rem;

      /* Initial scaling doesn't fit, when an icon is in front of the input */
      &:not(:placeholder-shown),
      &:focus {
        & + ${StyledLabel} {
          transform: scale(0.85)
            translate(
              ${(props) => props.theme.sizes.medium},
              ${(props) => props.theme.sizes.xsmall}
            );
        }
      }
    }
  }
`;
